import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../chrome/AppShell';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import { TCard, TFoot, Toolbar, TRow } from '../components/table';
import { ConfirmDialog, Dialog, Field, TextInput } from '../components/forms';
import { PageHeadActions } from '../components/nav';
import { Frow, ListState, useList, useToast } from '../components/roster';
import { dateRangeLabel } from '../lib/jobs';

// Address is plain text until the Places integration lands; Maps is a hand-off.

const maps = (address: string) =>
    `https://maps.apple.com/?q=${encodeURIComponent(address)}`;

export default function VenuesV3() {
    const api = useAuthenticatedApi();
    const navigate = useNavigate();
    const toast = useToast();
    const venues = useList(() => api.get('/production/venues').then((r: any) => r.data));
    const [query, setQuery] = useState('');

    const rows = useMemo(() => venues.rows ?? [], [venues.rows]);
    const q = query.trim().toLowerCase();
    const visible = useMemo(
        () =>
            rows
                .filter((v: any) => !q || [v.name, v.address].some((x) => (x ?? '').toLowerCase().includes(q)))
                .sort((a: any, b: any) => (b.lastActivationAt ?? '').localeCompare(a.lastActivationAt ?? '')),
        [rows, q],
    );

    const [opened, setOpened] = useState<any>(null);
    /* a late reply for another row (or a closed dialog) is ignored */
    const wanted = useRef<number | null>(null);
    const open = (v: any) => {
        wanted.current = v.id;
        setOpened({ ...v, loading: true });
        api.get(`/production/venues/${v.id}`)
            .then((r: any) => wanted.current === v.id && setOpened(r.data))
            .catch(() => wanted.current === v.id && setOpened({ ...v, loadFailed: true }));
    };
    const close = () => {
        wanted.current = null;
        setOpened(null);
    };

    const [removing, setRemoving] = useState<any>(null);
    const remove = async (v: any) => {
        venues.setRows((list) => (list ?? []).filter((x: any) => x.id !== v.id));
        try {
            await api.delete(`/production/venues/${v.id}`);
            toast.show(`Removed ${v.name}.`);
        } catch {
            venues.reload();
            toast.show(`Couldn’t remove ${v.name}.`);
        }
    };

    const [adding, setAdding] = useState(false);
    const [f, setF] = useState({ name: '', address: '' });
    const [saving, setSaving] = useState(false);
    const add = async () => {
        if (!f.name.trim() || saving) return;
        setSaving(true);
        try {
            await api.post('/production/venues', { name: f.name.trim(), address: f.address.trim() || null });
            setAdding(false);
            toast.show(`Added ${f.name.trim()}.`);
            setF({ name: '', address: '' });
            venues.reload();
        } catch {
            toast.show('Couldn’t add this venue.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <AppShell>
            <PageHeadActions
                title="Venues"
                sub={venues.rows ? `${rows.length} venues` : 'Places you run events'}
                actions={<button className="tb-new" onClick={() => setAdding(true)}>Add venue</button>}
            />
            <Toolbar searchValue={query} onSearch={setQuery} searchPlaceholder="Search name or address" filterCount={0} />
            <ListState
                rows={venues.rows}
                failed={venues.failed}
                what="venues"
                reload={venues.reload}
                empty={visible.length === 0 ? (rows.length === 0 ? 'No venues yet.' : `Nothing matches “${query}”.`) : undefined}
                emptyAction={rows.length === 0 ? 'Add venue' : undefined}
                onEmptyAction={() => setAdding(true)}
            >
                <TCard>
                    {visible.map((v: any, i: number) => {
                        const n = Number(v.activationCount ?? 0);
                        const last = v.lastActivationAt ? `Last event ${dateRangeLabel(v.lastActivationAt)}` : 'No events yet';
                        return (
                            <TRow
                                key={v.id}
                                index={i}
                                name={v.name}
                                meta={v.address ?? '—'}
                                metaDate={last}
                                date={last}
                                status={`${n} ${n === 1 ? 'event' : 'events'}`}
                                onOpen={() => open(v)}
                                menuItems={[
                                    { label: 'Details', onClick: () => open(v) },
                                    ...(v.address ? [{ label: 'Open in Maps', onClick: () => window.open(maps(v.address), '_blank') }] : []),
                                    { label: 'Remove…', onClick: () => setRemoving(v) },
                                ]}
                            />
                        );
                    })}
                </TCard>
            </ListState>
            {venues.rows && <TFoot>{q ? `${visible.length} of ${rows.length} · filtered` : `${rows.length} venues · most recent first`}</TFoot>}

            <Dialog
                open={opened !== null}
                title={opened?.name ?? ''}
                onClose={close}
                footer={<button type="button" className="tb-btn" onClick={close}>Close</button>}
            >
                {opened && (
                    <div className="person">
                        <p className="pg-sub" style={{ margin: '0 0 12px' }}>{opened.address ?? 'No address yet'}</p>
                        {opened.address && (
                            <div className="p-actions">
                                <a className="tb-btn" href={maps(opened.address)} target="_blank" rel="noreferrer">Open in Maps</a>
                            </div>
                        )}
                        <Frow label="Load-in">{opened.defaultLoadInInstructions || '—'}</Frow>
                        <Frow label="Parking">{opened.defaultParkingInstructions || '—'}</Frow>
                        <Frow label="Internet">
                            {opened.defaultInternetProvided == null
                                ? '—'
                                : opened.defaultInternetProvided
                                  ? opened.defaultInternetDetails || 'Provided'
                                  : 'Bring a hotspot'}
                        </Frow>
                        {opened.timezone && <Frow label="Timezone">{opened.timezone}</Frow>}
                        <p className="s-lab" style={{ margin: '14px 0 6px' }}>Events here</p>
                        {opened.loading ? (
                            <p className="pg-sub" style={{ margin: 0 }}>Loading…</p>
                        ) : opened.loadFailed ? (
                            <p className="pg-sub" style={{ margin: 0 }}>Couldn’t load events.</p>
                        ) : (opened.activations ?? []).length === 0 ? (
                            <p className="pg-sub" style={{ margin: 0 }}>No events here yet.</p>
                        ) : (
                            (opened.activations ?? []).map((a: any) => (
                                <a
                                    className="trow2 linkrow"
                                    key={a.activationId}
                                    href={`/activations/${a.activationId}`}
                                    onClick={(e) => { if (!e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate(`/activations/${a.activationId}`); } }}
                                >
                                    <span className="nm2">{a.name}</span>
                                    <span className="right">{dateRangeLabel(a.startAt)}</span>
                                </a>
                            ))
                        )}
                    </div>
                )}
            </Dialog>

            <Dialog
                open={adding}
                title="Add venue"
                onClose={() => setAdding(false)}
                onSubmit={add}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setAdding(false)}>Cancel</button>
                        <button type="submit" className="tb-new" disabled={!f.name.trim() || saving}>{saving ? 'Adding…' : 'Add venue'}</button>
                    </>
                }
            >
                <Field label="Name">
                    <TextInput autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                </Field>
                <Field label="Address (optional)" help="Address search arrives with the Places integration.">
                    <TextInput autoComplete="street-address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
                </Field>
            </Dialog>
            <ConfirmDialog
                open={removing !== null}
                title={`Remove “${removing?.name ?? ''}”?`}
                body={
                    Number(removing?.activationCount) > 0
                        ? `${Number(removing.activationCount)} ${Number(removing.activationCount) === 1 ? 'event references' : 'events reference'} this venue; ${Number(removing.activationCount) === 1 ? 'it keeps its' : 'they keep their'} details but lose the link. This can’t be undone.`
                        : 'This can’t be undone.'
                }
                actionLabel="Remove"
                onConfirm={() => removing && remove(removing)}
                onClose={() => setRemoving(null)}
            />
            {toast.node}
        </AppShell>
    );
}
